import { useMemo } from "react";
import { z } from "zod";
import {
  Box,
  CategoricalBarChart,
  ChartLegend,
  WidgetEmpty,
  WidgetSkeleton,
  compactNumber,
  truncateLabel,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
} from "@salestrak/ui";
import { AGE_STATUS_CONFIG } from "#app/lib/age-status";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { useStageThroughput, THROUGHPUT_SERIES, type ThroughputRow } from "./use-stage-throughput";

/**
 * `stage-throughput` — a themed multi-series bar chart of projects per stage split
 * by follow-up status, on the `CategoricalBarChart` finished chart. It exercises the
 * stacked/grouped modes AND a rich settings schema: a workflow `select`
 * (`dynamicOptions`), pipeline-vs-volume ordering, a top-N `number`, per-series
 * toggles, and a stacked/grouped mode — every field drives the render. The data
 * layer fans `useIssueGroupCounts` across the three age statuses (see
 * `use-stage-throughput`).
 */
export const stageThroughputConfigSchema = z
  .looseObject({
    workflow: z.string().nullable().default(null),
    sort: z.enum(["pipeline", "volume"]).default("pipeline"),
    maxStages: z.int().min(3).max(20).default(8),
    showOnTrack: z.boolean().default(true),
    showAtRisk: z.boolean().default(true),
    showOffTrack: z.boolean().default(true),
    showUntracked: z.boolean().default(true),
    mode: z.enum(["stacked", "grouped"]).default("stacked"),
    showGrid: z.boolean().default(true),
    showAxes: z.boolean().default(true),
    legend: z.boolean().default(true),
  });

export type StageThroughputConfig = z.infer<typeof stageThroughputConfigSchema>;

const VISIBLE_KEY = {
  onTrack: "showOnTrack",
  atRisk: "showAtRisk",
  offTrack: "showOffTrack",
  untracked: "showUntracked",
} as const;

function StageThroughputRender({ h, config }: WidgetRenderContext<StageThroughputConfig>) {
  const { data, loading, error } = useStageThroughput(config.workflow);
  // Click-through: a bar press opens this card's Data drawer on the stage's rows.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();
  const emptyTitle = useWindowEmptyTitle("No Projects");

  const series = useMemo(
    () => THROUGHPUT_SERIES.filter((s) => config[VISIBLE_KEY[s.key]] !== false),
    [config],
  );

  const rows = useMemo(() => {
    const sorted =
      config.sort === "volume" ? [...data].sort((a, b) => b.total - a.total) : data;
    return sorted.slice(0, Math.max(3, config.maxStages));
  }, [data, config.sort, config.maxStages]);

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="columns" />;
  }
  if (rows.length === 0 || series.length === 0) {
    return (
      <WidgetEmpty title={emptyTitle} />
    );
  }

  const compact = h < 3;
  const showAxes = config.showAxes && !compact;
  const showGrid = config.showGrid && !compact;
  const showLegend = config.legend && !compact;

  const handleBarClick =
    drill && instance
      ? (row: ThroughputRow) => {
          if (row.id)
            drill.openData(instance.instanceId, {
              kind: "dimension",
              dimension: "stage",
              ids: [row.id],
              label: row.label,
            });
        }
      : undefined;

  return (
    <Box direction="column" fill="flex" gap="xs">
      <Box fill="flex">
        <CategoricalBarChart<ThroughputRow>
          data={rows}
          categoryKey="label"
          series={series.map((s) => ({ key: s.key, colorToken: s.colorToken }))}
          mode={config.mode}
          showGrid={showGrid}
          showAxes={showAxes}
          // The value axis drops out on a short card, but the category axis is
          // what NAMES each bar — without it the card is anonymous stripes. Only
          // the user's own Axes toggle turns it off.
          showCategoryAxis={config.showAxes}
          angledCategoryTicks
          categoryTickFormatter={(v) => truncateLabel(v)}
          valueTickFormatter={compactNumber}
          onBarClick={handleBarClick}
          ariaLabel="Projects by stage and follow up"
          renderTooltip={(row) => (
            <WidgetChartTooltip
              label={row.label}
              details={series.map((s) => `${s.label}: ${row[s.key]}`)}
              footer={`${row.total.toLocaleString()} total`}
            />
          )}
        />
      </Box>
      {showLegend ? (
        <ChartLegend
          items={series.map((s) => ({ label: s.label, colorToken: s.colorToken }))}
          justify="center"
        />
      ) : null}
    </Box>
  );
}

export const stageThroughputDef: WidgetModule<StageThroughputConfig> = {
  type: "stage-throughput",
  title: "Stage throughput",
  icon: "bar-chart",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 6 },
  configSchema: stageThroughputConfigSchema,
  headerActions: WidgetHeaderFilters,
  settings: [
    {
      title: "Source",
      fields: [
        { key: "workflow", kind: "workflowSource", label: "Workflow" },
      ],
    },
    {
      title: "Data",
      fields: [
        {
          key: "sort",
          kind: "segmented",
          label: "Order",
          options: [
            { value: "pipeline", label: "Stage order" },
            { value: "volume", label: "Count" },
          ],
        },
        { key: "maxStages", kind: "number", label: "Max stages", min: 3, max: 20, step: 1 },
      ],
    },
    {
      title: "Series",
      fields: [
        { key: "showOnTrack", kind: "toggle", label: AGE_STATUS_CONFIG.onTrack.label },
        { key: "showAtRisk", kind: "toggle", label: AGE_STATUS_CONFIG.atRisk.label },
        { key: "showOffTrack", kind: "toggle", label: AGE_STATUS_CONFIG.offTrack.label },
        { key: "showUntracked", kind: "toggle", label: AGE_STATUS_CONFIG.disabled.label },
      ],
    },
    {
      title: "Display",
      fields: [
        {
          key: "mode",
          kind: "segmented",
          label: "Bars",
          options: [
            { value: "stacked", label: "Stacked" },
            { value: "grouped", label: "Grouped" },
          ],
        },
        { key: "showGrid", kind: "toggle", label: "Gridlines" },
        { key: "showAxes", kind: "toggle", label: "Axes" },
        { key: "legend", kind: "toggle", label: "Legend" },
      ],
    },
  ],
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  // No gallery card: Breakdown by stage already reads Projects per stage with
  // the Stale share, and "throughput" promised a rate this never measured.
  // Rows placed before the card left keep rendering.
  catalog: [],
  render: StageThroughputRender,
};
